import { type Component, createApp, watch } from 'vue';
import 'iconify-icon';
import { createPinia, type Pinia } from 'pinia';
import VueDragscroll from 'vue-dragscroll';
import Toast, { POSITION } from 'vue-toastification';

import { useConnectionStore } from '@/stores/connection';
import { useRootStore } from '@/stores/root';
import { connection, permissions } from '@vp/vue-components';
import { createGettext } from 'vue3-gettext';
import type { API } from '@vp/common';

/**
 * The account the host page named, or the reason there isn't one.
 *
 * `rootStore.currentAccountId` is `0` both when a host page named no account and when there is no
 * host page at all, and only `window.vpAppConfig` tells those apart. The connection store treats
 * them very differently, so the distinction has to survive: `undefined` is standalone, where
 * `AppStandalone` will resolve the account and requests are held until it does; `null` is a host
 * page that named none, which is a settled answer and fails those requests at once rather than
 * hanging each of them for the store's full timeout with nothing left to resolve it.
 */
const hostAccountId = (currentAccountId: number): number | null | undefined => {
  if (typeof window === 'undefined' || !window.vpAppConfig) {
    return undefined;
  }

  return currentAccountId || null;
};

const initialisedPinias = new WeakSet<Pinia>();
let sharedPinia: Pinia | undefined;

// Shared by every embedded element, so what the Ticketing modal saves reaches the page's lists.
export const elementPinia = (): Pinia => (sharedPinia ??= createPinia());

type InstallOptions = {
  middleware?: API.Client.InitClientParams['middleware'];
};

export const installPlugins = (app: import('vue').App, pinia: Pinia = createPinia(), options: InstallOptions = {}) => {
  const gettext = createGettext({
    availableLanguages: {
      en: 'English',
      fr: 'Français',
      es: 'Español',
      // de: 'Deutsch',
    },
    defaultLanguage: 'en',
  });

  app
    .use(gettext)
    .use(pinia)
    .use(VueDragscroll)
    .use(Toast, {
      position: POSITION.TOP_RIGHT,
      timeout: 3000,
      hideProgressBar: true,
      closeButton: false,
      transition: 'Vue-Toastification__fade',
    });

  // A later element must not reset the account, options or locale an earlier one settled.
  if (initialisedPinias.has(pinia)) {
    return app;
  }
  initialisedPinias.add(pinia);

  const rootStore = useRootStore();

  // `requireAccount` opts the admin app into the connection store refusing to send an
  // account-scoped operation without `X-VP-Account-Id`. Only this app asks for it: checkout builds
  // its clients from the same store, has no signed-in user for the backend to mis-default to, and
  // takes its tenancy from the event.
  const initOptions = {
    apiBase: rootStore.apiBase,
    apiEndpoint: rootStore.apiGraphqlEndpoint,
    gqlApiApp: 'admin',
    requireAccount: true,
    middleware: options.middleware,
  };

  const connectionStore = useConnectionStore();
  connectionStore.init(initOptions);

  const vpConnectionStore = connection.useConnectionStore();
  vpConnectionStore.init(initOptions);

  // Embedded, the Rails host names the account on the page it serves, and the root store reads it
  // synchronously — so the account is known here, before anything below issues a request. Nothing
  // used to set it in this mode at all, which is why every request from the deployed app went out
  // unscoped and was answered by whichever account the backend happened to default to.
  //
  // The two "no account" cases are told apart on purpose, because the connection store treats them
  // very differently. No host config at all is standalone: `AppStandalone` resolves the account, so
  // this stays `undefined` and requests issued in the meantime are held until it does. A host
  // config that names no account is a settled answer, so it is `null` and those requests fail
  // immediately — leaving it `undefined` there would hang every one of them for the store's full
  // timeout, forever, with nothing left to resolve it.
  vpConnectionStore.accountId = hostAccountId(rootStore.currentAccountId);

  watch(() => gettext.current, newLocale => vpConnectionStore.setLocale(newLocale), { immediate: true });

  void permissions.usePolicy().ensurePermissions();

  return app;
};

const initApp = (rootComponent: Component, options: InstallOptions = {}) => {
  const app = createApp(rootComponent);

  installPlugins(app, undefined, options);

  return app;
};

export default initApp;
